import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import TuneIcon from "@mui/icons-material/Tune";
import { Card, CardContent, Stack, Typography } from "@mui/material";
import * as React from "react";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { ParamSet } from "../../../types";
import { formatNumber } from "../../../utils";
import { feetToMeters } from "../../../utils/math";
import { StackStrategy } from "../GenerateParamSets";

type Props = {
  paramSet: ParamSet;
  stackStrategy: StackStrategy;
  setStackStrategy: (stackStrategy: StackStrategy) => void;
};

export const SelectStackStrategy: React.FC<Props> = ({
  paramSet,
  stackStrategy,
  setStackStrategy,
}) => {
  const { activeFBO } = useActiveFBOs();

  // Helper function to format distance values with proper units
  const formatDistance = (valueInFeet: number | null | undefined): string => {
    if (valueInFeet == null) return "N/A";

    const useMetric = activeFBO?.settings?.useMetricSystem || false;
    if (useMetric) {
      const valueInMeters = feetToMeters(valueInFeet);
      return `${formatNumber(valueInMeters, 1)} m`;
    }
    return `${formatNumber(valueInFeet, 0)} ft`;
  };

  // Helper function to format vertical spacing display
  const formatVerticalSpacing = (overlap: boolean, spacing: number | null | undefined): string => {
    if (!overlap) return "No Overlap";
    if (spacing == null) return "N/A";
    return formatDistance(spacing);
  };

  return (
    <Stack direction="column">
      <Stack direction="row" spacing={2}>
        <Card
          elevation={0}
          variant="outlined"
          onClick={() => setStackStrategy(StackStrategy.normal)}
          sx={{
            width: "100%",
            borderColor:
              stackStrategy === StackStrategy.normal && "success.main",
            borderWidth: stackStrategy === StackStrategy.normal && 2,
            cursor: "pointer",
          }}
        >
          <CardContent>
            <ThumbUpIcon />
            <Typography>Normal</Typography>
            <Typography variant="caption">
              Day-to-day operations using SOPs.
              <br />
              • Horizontal spacing: {formatDistance(paramSet.options.horizontal_spacing)}
              <br />
              • Overlap min: {formatVerticalSpacing(
                activeFBO?.sop_overlap || false,
                activeFBO?.sop_vertical_spacing
              )}
            </Typography>
          </CardContent>
        </Card>
        <Card
          elevation={0}
          variant="outlined"
          onClick={() => setStackStrategy(StackStrategy.tight)}
          sx={{
            width: "100%",
            borderColor:
              stackStrategy === StackStrategy.tight && "success.main",
            borderWidth: stackStrategy === StackStrategy.tight && 2,
            cursor: "pointer",
          }}
        >
          <CardContent>
            <Stack direction="row">
              <LocalFireDepartmentIcon />
            </Stack>
            <Typography>Tight</Typography>
            <Typography variant="caption">
              Optimized for maximum utilization.
              <br />
              • Horizontal spacing: {formatDistance(
                activeFBO?.sop_horizontal_spacing_tight ?? paramSet.options.horizontal_spacing
              )}
              <br />
              • Overlap min: {formatVerticalSpacing(
                activeFBO?.sop_overlap_tight || false,
                activeFBO?.sop_vertical_spacing_tight
              )}
            </Typography>
          </CardContent>
        </Card>
      </Stack>
      <Stack direction="row" spacing={2}>
        <Card
          elevation={0}
          variant="outlined"
          onClick={() => setStackStrategy(StackStrategy.loose)}
          sx={{
            width: "100%",
            borderColor:
              stackStrategy === StackStrategy.loose && "success.main",
            borderWidth: stackStrategy === StackStrategy.loose && 2,
            cursor: "pointer",
          }}
        >
          <CardContent>
            <BeachAccessIcon />
            <Typography>Loose</Typography>
            <Typography variant="caption">
              Optimized for easy towing, at the expense of utilization.
              <br />
              • Horizontal spacing: {formatDistance(
                activeFBO?.sop_horizontal_spacing_loose
              )}
              <br />
              • Overlap min: {formatVerticalSpacing(
                activeFBO?.sop_overlap_loose || false,
                activeFBO?.sop_vertical_spacing_loose
              )}
            </Typography>
          </CardContent>
        </Card>
        <Card
          elevation={0}
          variant="outlined"
          onClick={() => setStackStrategy(StackStrategy.custom)}
          sx={{
            width: "100%",
            borderColor:
              stackStrategy === StackStrategy.custom && "success.main",
            borderWidth: stackStrategy === StackStrategy.custom && 2,
            cursor: "pointer",
          }}
        >
          <CardContent>
            <TuneIcon />
            <Typography>Custom</Typography>
            <Typography variant="caption">
              Completely customize your stack preferences.
            </Typography>
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  );
};